import React, { useEffect, useMemo, useState } from 'react';
import { Col, Form, notification, Row } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useNavigate, useParams } from 'react-router';
import moment from 'moment';
import { useQuery } from 'react-query';
import { ThunderboltTwoTone } from '@ant-design/icons';
import { sumBy } from 'lodash';

import DF from '../../../DisplayFramework';
import DocList from '../../Document/DocList';
import Button from 'components/Button';
import SaveButton from 'components/SaveButton';
import WizardTabs from 'components/WizardTabs';
import Page, { PageModalProps } from 'components/Page';
import {
  createClientInvoice,
  getClientInvoiceById,
  updateClientInvoice,
} from 'services/clientInvoice';
import { getMyWizards } from 'services/workflow';
import { ArrowLeftOutlined } from '@ant-design/icons';
import TabClientInvoice from './components/tabClientInvoice';
import TabClientInvoiceLines from './components/tabClientInvoiceLines';
import TabClientInvoiceSchedule from './components/tabClientInvoiceSchedule';
import TabClientInvoiceFees from './components/tabClientInvoiceFees';
import TabClientInvoiceNetting from './components/tabClientInvoiceNetting';
import {
  downloadBlobFile,
  shouldUpdateCompare,
  toDatePickerDate,
} from '../../../utils/utils2';
import TasksListPopover from '../../workflow/TasksListPopover';
import { onFinishFailed } from 'components/DisplayValidationErrors/DisplayValidationErrors';
import Title from 'components/Title';
import IActions from 'icons/IActions';
import GetLabel from 'DisplayFramework/components/GetLabel';
import TabClientInvoiceLateCharge from './components/tabClientInvoiceLateCharge';
import TabClientInvoicePrefinInterest from './components/tabClientInvoicePrefinInterest';
import TabCiVarInterest from './components/tabCiVarInterest';
import {
  genClientInvoicePDFandEmail,
  GenClientInvoicePDFandEmailParams,
} from 'services/printingService';
import { FilePdfTwoTone } from '@ant-design/icons';
import { API_URLS } from 'utils/config';

type CIDetailProps = PageModalProps & {
  ciid?: number;
  onSave?: (clientInvoice: any) => void;
  clientInvoiceProp?: any;
  isCreateCI?: boolean | undefined;
  showButtons?: boolean;
};

const CIDetail = (props: CIDetailProps) => {
  const {
    onSave,
    clientInvoiceProp,
    visible = false,
    onCancel,
    showAsModal = false,
    showButtons = true,
    isCreateCI,
  } = props;

  const [form] = useForm();
  const navigate = useNavigate();
  const { idOrCreate } = useParams<{
    idOrCreate: string;
  }>();

  const isCreate = isCreateCI ?? idOrCreate === 'create';

  form.setFieldsValue({ isCreate: isCreate });

  const { data: wizData } = useQuery(['getMyWizards', 'CINVOICE'], () =>
    getMyWizards({ entity: 'CINVOICE' }).then((res) => res.data)
  );

  const [refetechnetting, setrefetechnetting] = useState(false);
  const getClientInvoiceId = () => {
    const ciid = props.ciid ? props.ciid : idOrCreate;
    if (isCreate) {
      return;
    }
    return Number(ciid);
  };
  const ciid = getClientInvoiceId();

  const { data, refetch } = useQuery(
    [showAsModal ? 'getClientInvoiceByIdModal' : 'getClientInvoiceById', ciid],
    () =>
      getClientInvoiceById({ ciid: Number(ciid) }).then((res) => {
        if (res.code === '403') {
          history.push('/403')

        }
        return res.data;
      }),
    {
      enabled: ciid !== undefined,
    }
  );

  const clientInvoice = clientInvoiceProp ?? data;
  const tabPosition = localStorage.getItem('tabposition');

  useEffect(() => {
    if (clientInvoice) {
      form.setFieldsValue({
        ...clientInvoice,
        flagcn: clientInvoice.ciinvoicetype === 'CN' ? true : false,
        thirdparty: {
          tpid: clientInvoice.tpidclient,
          tpname: clientInvoice.clientname,
          tpreference: clientInvoice.clientreference,
        },
        ciltype:
          clientInvoice.citype === 'MISCFEES' ? 'Fees' : clientInvoice.citype,
        cidocdate: toDatePickerDate(clientInvoice.cidocdate),
        cidocreference:
          clientInvoice.citype === 'FIRSTRENT' ||
          clientInvoice.citype === 'RENTAL'
            ? clientInvoice.cireference
            : clientInvoice.cidocreference,
        cisamount: sumBy(clientInvoice.clientInvoiceSchedules, 'cisamount'),
      });
    }
  }, [clientInvoice, form]);
  const [loadingBtn, setLoadingBtn] = useState<boolean>(false);
  const onFinish = (values: any) => {
    const clientInvoiceLines = form.getFieldValue('clientInvoiceLines');
    const clientInvoiceSchedules = form.getFieldValue('clientInvoiceSchedules');
    const cigrosstotal = form.getFieldValue('cigrosstotal');
    const cinettotal = form.getFieldValue('cinettotal');
    const citaxtotal = form.getFieldValue('citaxtotal');
    const tpidclient = form.getFieldValue('thirdparty').tpid;
    const ctrid = form.getFieldValue('ctrid');

    const ctreference = form.getFieldValue('ctreference');
    const citype = form.getFieldValue('citype');
    const cistatus = form.getFieldValue('cistatus');
    const cisamount = form.getFieldValue('cisamount');
    const cimultiplecontracts = form.getFieldValue('cimultiplecontracts')
      ? form.getFieldValue('cimultiplecontracts')
      : false;

    const data = {
      ...values,
      ciid: ciid || '',
      clientInvoiceLines,
      clientInvoiceSchedules,
      ctrid: ctrid,
      ctreference,
      cistatus,
      cigrosstotal,
      cinettotal,
      citaxtotal,
      cimultiplecontracts,
      cireference: '',
      ciinvoicetype: '',
      tpidclient: tpidclient,
      citype,
      mgid: localStorage.getItem('mgid'),
      cidueamount: cigrosstotal,
      jobid: 0,
      accountingdate: toDatePickerDate(moment().format('YYYY-MM-DD')),
      last_action: ciid ? wizData?.taskidUpdate : wizData?.taskidCreate,
    };

    if (onSave) {
      onSave(data);
      return;
    }
    if (clientInvoiceLines.length === 0) {
      notification.error({
        message: 'Error validating input',
        description: 'Please add Client Invoice Line',
      });

      return;
    }

    if (cigrosstotal !== cisamount) {
      notification.error({
        message: 'Error validating input',
        description:
          'The total Payment Shcedule and the Total lines are different. please check',
      });
      return;
    }
    setLoadingBtn(true);
    if (ciid) {
      updateClientInvoice({ clientInvoiceRequest: data, ciid })
        .then((res) => {
          navigate(`/client-invoice/${res.data.ciid}`);
          notification.success({ message: res.message });
          setLoadingBtn(false);
        })
        .catch(() => {
          setLoadingBtn(false);
        });
    } else {
      createClientInvoice({ clientInvoiceRequest: data })
        .then((res) => {
          if (res.code === '200') {
            notification.success({ message: res.message });
            navigate(`/client-invoice/${res.data.ciid}`);
            refetch();
            setLoadingBtn(false);
          } else {
            setLoadingBtn(false);

            notification.error({ message: res.message });
          }
        })
        .catch(() => {
          setLoadingBtn(false);
        });
    }
  };
  const initialSearchParams: GenClientInvoicePDFandEmailParams = {
    cirefstart: data?.cireference || '',
    cirefend: data?.cireference || '',
    mgid: Number(localStorage.getItem('mgid')) ?? 1,
    groupingLines: 'NONE',
    jobid: '' as any,
    printingmode: data?.printid ? 'REPRINTALL' : 'PRINT',
    emailmode: 'NONE',
    Authorization: '',
    flagEditAllInOne: false,
  };
  console.log({ initialSearchParams });
  const [searchParams, setSearchParams] =
    useState<GenClientInvoicePDFandEmailParams>(initialSearchParams);
  const Run = (values: any) => {
    setSearchParams((prev) => ({
      ...prev,
      ...values,
      cirefstart: 'test',
      cirefend: data?.cireference,
    }));
    console.log(data?.cireference, searchParams);
    setTimeout(refetching, 0);
  };
  const params = useMemo(() => ({ ...searchParams }), [searchParams]);

  const {
    data: jobResponse,
    refetch: refetching,
    isFetching,
  } = useQuery(
    ['genClientInvoicePDFandEmail', initialSearchParams],
    () =>
      genClientInvoicePDFandEmail(initialSearchParams).then((res) => {
        console.log({ params, searchParams, initialSearchParams });
        // notification.info({
        //   message: res.message,
        //   placement: 'bottomRight',
        // });
        return res;
      }),
    {
      enabled: false,
    }
  );
  const PRINTING_URL = API_URLS.PRINTING;
  const getUrl = (printid: number) =>
    `${PRINTING_URL}/api/print/download?printid=${printid}`;

  return (
    <DF.Provider pagcode="cidetail" entity="CINVOICE" entityid={ciid}>
      <Page
        modalTitle="Client Invoice details"
        showAsModal={showAsModal}
        visible={visible}
        onCancel={onCancel}
        centered
      >
        <Form
          layout="vertical"
          form={form}
          initialValues={INITIAL_VALUES}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          <Row>
            <Col span={16}>
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                {!showAsModal && (
                  <ArrowLeftOutlined
                    onClick={() => {
                      navigate(`/client-invoice`);
                    }}
                    style={{
                      float: 'left',
                      margin: '0px 5px',
                      fontSize: 'larger',
                    }}
                  />
                )}
                {ciid ? (
                  <>
                    <GetLabel label="ClientInvoice" />
                    {' - ' +
                      clientInvoice?.cireference +
                      ' - ' +
                      clientInvoice?.clientname}{' '}
                  </>
                ) : (
                  <Title level={4} comcode="NewCI"></Title>
                )}
              </div>
            </Col>
            {showButtons && (
              <Col span={8}>
                {tabPosition === 'left' ? (
                  <>
                    {isCreate ? (
                      <Row>
                        <Col span={16}>
                          {Number(ciid) ? (
                            <TasksListPopover
                              entityId={Number(ciid)}
                              entityName="CINVOICE"
                              entityReference={data?.cireference}
                              title={
                                data?.cireference + ' - ' + data?.clientname
                              }
                              onClose={() => {
                                setrefetechnetting(!refetechnetting);
                                refetch();
                              }}
                            >
                              <Button comcode="actions" icon={<IActions />} />
                            </TasksListPopover>
                          ) : null}
                        </Col>

                        <Col span={8}>
                          <SaveButton
                            comcode="save"
                            custom={{ height: 52 }}
                            loading={loadingBtn}
                          />
                        </Col>
                      </Row>
                    ) : (
                      <Row gutter={8}>
                        <Col span={data?.printid ? 9 : 14}></Col>
                        {data?.printid ? (
                          <Col span={5}>
                            <Button
                              size="small"
                              onClick={(ev) => {
                                console.log({ ev });
                                downloadBlobFile(
                                  data.cireference + '.pdf',
                                  getUrl(data.printid)
                                );
                              }}
                              icon={<FilePdfTwoTone twoToneColor="blue" />}
                              comcode="invoice"
                            />
                          </Col>
                        ) : (
                          ''
                        )}

                        <Col span={5}>
                          <Button
                            loading={isFetching}
                            onClick={Run}
                            comcode={data?.printid ? 'reprint' : 'print'}
                            type="primary"
                          />
                        </Col>
                        <Col span={5}>
                          {Number(ciid) ? (
                            <TasksListPopover
                              entityId={Number(ciid)}
                              entityName="CINVOICE"
                              entityReference={data?.cireference}
                              title={
                                data?.cireference + ' - ' + data?.clientname
                              }
                              onClose={() => {
                                setrefetechnetting(!refetechnetting);
                                refetch();
                              }}
                            >
                              <Button comcode="actions" icon={<IActions />} />
                            </TasksListPopover>
                          ) : null}
                        </Col>
                      </Row>
                    )}{' '}
                  </>
                ) : null}
              </Col>
            )}
          </Row>

          <WizardTabs
            entity="CINVOICE"
            entityid={ciid ?? clientInvoice?.ciid}
            taskid={ciid ? wizData?.taskidDisplay : wizData?.taskidCreate}
            defaultTabs={['tabClientInvoice', 'tabClientInvoiceLines']}
            tabs={{
              tabCiVarInterestDetail: (
                <TabCiVarInterest
                  ciid={ciid}
                  type={clientInvoice?.ciinvoicetype}
                  currcode={clientInvoice?.currcode}
                />
              ),
              tabCiPrefinInterest: (
                <TabClientInvoicePrefinInterest
                  ciid={ciid}
                  type={clientInvoice?.ciinvoicetype}
                  currcode={clientInvoice?.currcode}
                />
              ),
              tabCilatecharge: (
                <TabClientInvoiceLateCharge
                  ciid={ciid}
                  type={clientInvoice?.ciinvoicetype}
                />
              ),
              tabClientInvoice: <TabClientInvoice form={form} />,
              tabClientInvoiceLines: (
                <Form.Item
                  noStyle
                  shouldUpdate={shouldUpdateCompare('cimultiplecontracts')}
                >
                  {({ getFieldValue }) => (
                    <TabClientInvoiceLines
                      form={form}
                      ciid={ciid}
                      cimultiplecontracts={getFieldValue('cimultiplecontracts')}
                    />
                  )}
                </Form.Item>
              ),
              tabClientInvoiceSchedule: (
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => (
                    <TabClientInvoiceSchedule form={form} ciid={ciid} />
                  )}
                </Form.Item>
              ),
              tabClientInvoiceFees: <TabClientInvoiceFees form={form} />,
              tabClientInvoiceNetting: (
                <TabClientInvoiceNetting
                  showAsModal={showAsModal}
                  refetchNetting={refetechnetting}
                  form={form}
                  ciid={ciid}
                />
              ),
              tabUpload: (
                <DocList
                  objectid={ciid}
                  objecttype="CINVOICE"
                  categorie="DOC"
                />
              ),
            }}
            tabBarExtraContent={
              tabPosition === 'top' ? (
                ciid ? (
                  <TasksListPopover
                    onClose={() => {
                      setrefetechnetting(!refetechnetting);
                      refetch();
                    }}
                    title={data?.cireference + ' - ' + data?.clientname}
                    entityId={ciid}
                    entityReference={data?.cireference}
                    entityName="CINVOICE"
                  >
                    <Button
                      icon={<ThunderboltTwoTone twoToneColor="#eb2f96" />}
                      size="middle"
                      comcode="actions"
                    />
                  </TasksListPopover>
                ) : (
                  <SaveButton comcode="save" loading={loadingBtn} />
                )
              ) : null
            }
          />
        </Form>
      </Page>
    </DF.Provider>
  );
};

const INITIAL_VALUES: any = {
  cigrosstotal: 0,
  cinettotal: 0,
  citaxtotal: 0,
  cidocdate: moment(),
  ctreference: null,
};

export default CIDetail;
